import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Header */}
      <header className="relative flex justify-between items-center p-6 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <img src="/quantumx2.png" alt="QuantumXAI" className="h-12 cursor-pointer" onClick={() => navigate('/')} />
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </button>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-gray-400 mb-8">Last Updated: November 11, 2025</p>

        <div className="prose prose-invert max-w-none space-y-8">
          {/* Agreement to Terms */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">Agreement to Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              By accessing or using QuantumXAI ("QXAI," "we," "us," or "our") at quantumxai.web.app (the "Service"), 
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
            </p>
          </section>

          {/* 1. Service Description */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Service Description</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-blue-400">1.1 Overview</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              QXAI is a privacy-first AI platform that provides access to multiple AI models for text generation, 
              image creation, video generation, and other AI-powered features.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">1.2 Core Features</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>Multi-model AI access (text, image, video)</li>
              <li>Hash-based authentication for privacy</li>
              <li>Credit-based usage system</li>
              <li>Subscription and one-time credit purchase options</li>
              <li>Cloud storage for chat history (optional)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">1.3 Service Availability</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong>Uptime:</strong> We strive for 99.9% uptime but do not guarantee uninterrupted service</li>
              <li><strong>Maintenance:</strong> Scheduled maintenance will be announced in advance when possible</li>
              <li><strong>Beta Features:</strong> Some features may be in beta and subject to change</li>
            </ul>
          </section>

          {/* 2. Account Registration */}
          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Account Registration and Security</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-blue-400">2.1 Account Creation</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>You must be at least 18 years old to create an account</li>
              <li>You must provide a valid email address for authentication</li>
              <li>Your email is hashed locally; our application servers identify your account by that hash, while Firebase Authentication handles your email and password for sign-in</li>
              <li>One account per user; multiple accounts are prohibited</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">2.2 Account Security</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>You are responsible for maintaining the confidentiality of your password</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>We are not liable for losses caused by unauthorized use of your account</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">2.3 Account Termination</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong>Cancel Subscription:</strong> You may cancel your subscription at any time; you keep access until the end of your paid period</li>
              <li><strong>Delete Account:</strong> You may permanently delete your account at any time through Settings</li>
              <li><strong>No Refunds:</strong> Deleting your account forfeits all remaining subscription time and credits without refund</li>
              <li><strong>Our Rights:</strong> We may suspend or terminate accounts that violate these Terms</li>
              <li><strong>Data Deletion:</strong> Upon account deletion, your data will be deleted per our Privacy Policy</li>
              <li><strong>No Refunds on Violation:</strong> No refunds for accounts terminated for Terms violations</li>
            </ul>
          </section>

          {/* 3. Subscription Plans and Pricing - CRITICAL SECTION */}
          <section className="bg-gray-800/50 p-6 rounded-lg border border-blue-500/30">
            <h2 className="text-2xl font-semibold mb-4">3. Subscription Plans and Pricing</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-blue-400">3.1 Subscription Tiers</h3>
            <p className="text-gray-300 mb-2">We offer three subscription tiers:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li><strong>Starter:</strong> 160 credits per month</li>
              <li><strong>Pro:</strong> 320 credits per month</li>
              <li><strong>Super Pro:</strong> 400 credits per month</li>
            </ul>
            <p className="text-gray-300 mb-4">Each tier includes monthly and annual billing options.</p>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">3.2 Billing Periods</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li><strong>Monthly:</strong> Billed every 30 days</li>
              <li><strong>Annual:</strong> Billed every 365 days (5% discount applied)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-yellow-400">⚠️ 3.3 Pricing Subject to Change</h3>
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg mb-4">
              <p className="text-yellow-200 font-semibold mb-2">IMPORTANT:</p>
              <p className="text-gray-300 leading-relaxed mb-3">
                We reserve the right to modify our pricing at any time. When we change pricing:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li><strong>Existing Subscriptions:</strong> Current subscribers will receive 30 days' notice before any price increase affects their account. Your current rate will be honored until the end of your current billing period.</li>
                <li><strong>New Subscriptions:</strong> New subscribers will pay the current advertised price at the time of purchase.</li>
                <li><strong>Grandfathering:</strong> We may, at our sole discretion, grandfather existing subscribers at their current rate, but we are not obligated to do so.</li>
                <li><strong>Notification:</strong> Price changes will be communicated via email (if provided), in-app notification, and updates to our pricing page.</li>
                <li><strong>Right to Cancel:</strong> If you do not agree to a price increase, you may cancel your subscription before the new price takes effect.</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">3.4 Credit Packages</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li><strong>Small Pack:</strong> 100 credits for $15.00</li>
              <li><strong>Medium Pack:</strong> 250 credits for $30.00</li>
              <li><strong>Large Pack:</strong> 500 credits for $50.00</li>
            </ul>
            <p className="text-gray-300 mb-4">One-time purchase credits never expire but are subject to the pricing change policy above.</p>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">3.5 Automatic Renewal</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>Subscriptions automatically renew at the end of each billing period</li>
              <li>You will be charged using your payment method on file</li>
              <li>You may cancel automatic renewal at any time before the next billing date</li>
              <li><strong>Cancellation takes effect at the end of the current paid billing period</strong></li>
              <li><strong>No refunds or prorated charges upon cancellation</strong></li>
              <li>You will receive email notification (if provided) before each renewal charge</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">3.6 Payment Processing</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
              <li>All payments are processed through Stripe, Inc.</li>
              <li>You must provide valid payment information</li>
              <li>Failed payments may result in service suspension</li>
              <li>You are responsible for all applicable taxes</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 text-blue-400">3.7 Free Tier</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>Limited free access may be provided at our discretion</li>
              <li>Free tier features and limits subject to change without notice</li>
              <li>No guarantee of continued free access</li>
            </ul>
          </section>

          {/* Contact */}
          <section className="bg-gray-800/30 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <ul className="text-gray-300 space-y-2">
              <li><strong>Email:</strong> legal@quantumxai.com</li>
              <li><strong>Support:</strong> support@quantumxai.com</li>
            </ul>
          </section>

          {/* Footer note */}
          <div className="text-center text-gray-500 text-sm pt-8 border-t border-gray-800">
            <p>By using QuantumXAI, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
